import { motion } from 'framer-motion';
import { BarChart3, CreditCard, PieChart, TrendingUp } from 'lucide-react';
import { useEffect, useState } from 'react';

interface StripeLoadingProps {
  onMinimumDurationComplete?: () => void;
}

const StripeLoading: React.FC<StripeLoadingProps> = ({ onMinimumDurationComplete }) => {
  // Progress state management
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(() => {
    const saved = Number(sessionStorage.getItem('stripe-loading-progress'));

    return Number.isFinite(saved) && saved >= 0 ? saved : 0;
  });
  const [minimumDurationComplete, setMinimumDurationComplete] = useState(false);

  // Clean loading steps
  const steps = [
    {
      text: 'Loading data...',
      duration: 5000,
      icon: CreditCard,
      color: 'text-purple-600',
    },
    {
      text: 'Processing metrics...',
      duration: 5000,
      icon: BarChart3,
      color: 'text-blue-600',
    },
    {
      text: 'Calculating totals...',
      duration: 5000,
      icon: TrendingUp,
      color: 'text-teal-600',
    },
    {
      text: 'Almost ready...',
      duration: 5000,
      icon: PieChart,
      color: 'text-green-600',
    },
  ];

  // Stripe-themed color system based on progress
  const getProgressColors = (progress: number) => {
    if (progress <= 25) {
      // Purple phase (0-25%) - Stripe brand color
      return {
        gradient: 'from-purple-600 via-indigo-600 to-blue-600',
        glow: 'from-purple-500 via-indigo-500 to-blue-500',
        border: 'border-purple-500',
        dot: 'bg-purple-500',
        ring: 'ring-purple-500/20',
      };
    } else if (progress <= 50) {
      // Blue to teal phase (26-50%)
      return {
        gradient: 'from-blue-600 via-cyan-600 to-teal-600',
        glow: 'from-blue-500 via-cyan-500 to-teal-500',
        border: 'border-blue-500',
        dot: 'bg-blue-500',
        ring: 'ring-blue-500/20',
      };
    } else if (progress <= 75) {
      // Teal to emerald phase (51-75%)
      return {
        gradient: 'from-teal-600 via-emerald-600 to-green-600',
        glow: 'from-teal-500 via-emerald-500 to-green-500',
        border: 'border-teal-500',
        dot: 'bg-teal-500',
        ring: 'ring-teal-500/20',
      };
    } else {
      // Green completion phase (76-100%)
      return {
        gradient: 'from-green-600 via-emerald-600 to-teal-600',
        glow: 'from-green-500 via-emerald-500 to-teal-500',
        border: 'border-green-500',
        dot: 'bg-green-500',
        ring: 'ring-green-500/20',
      };
    }
  };

  const colors = getProgressColors(progress);

  // Persist progress across re-mounts so the bar never jumps backwards
  useEffect(() => {
    if (progress >= 100) {
      sessionStorage.removeItem('stripe-loading-progress');
    } else {
      // Cap at 99 so we still show completion animation when ready
      sessionStorage.setItem('stripe-loading-progress', String(Math.min(99, progress)));
    }
  }, [progress]);

  // Minimum duration timer (20 seconds total for initial load, immediate for refresh)
  useEffect(() => {
    const duration = onMinimumDurationComplete ? 5000 : 2000; // 20s for initial, 5s for refresh
    const minimumTimer = setTimeout(() => {
      setMinimumDurationComplete(true);
      onMinimumDurationComplete?.();
    }, duration);

    return () => clearTimeout(minimumTimer);
  }, [onMinimumDurationComplete]);

  // Animate progress through steps
  useEffect(() => {
    let progressTimer: NodeJS.Timeout;
    let stepTimer: NodeJS.Timeout;

    const animateStep = (stepIndex: number) => {
      if (stepIndex >= steps.length) {
        // Ensure we reach 100% at the end
        setProgress(100);

        return;
      }

      setCurrentStep(stepIndex);
      const stepDuration = steps[stepIndex].duration; // 5000ms
      const baseStart = (stepIndex / steps.length) * 100; // 0, 25, 50, 75
      const startProgress = Math.max(baseStart, progress); // never go backwards within a step
      const endProgress = ((stepIndex + 1) / steps.length) * 100; // 25, 50, 75, 100

      // Set initial progress for this step
      setProgress(prev => Math.max(prev, startProgress));

      // Animate progress within this step (25% per 5 seconds)
      const progressRange = endProgress - startProgress; // Always 25%
      const updateInterval = 100; // Update every 100ms for smoother animation
      const totalUpdates = stepDuration / updateInterval; // 50 updates over 5 seconds
      const incrementPerUpdate = progressRange / totalUpdates; // 0.5% per update

      let updateCount = 0;

      progressTimer = setInterval(() => {
        updateCount++;
        const newProgress = startProgress + incrementPerUpdate * updateCount;

        if (newProgress >= endProgress || updateCount >= totalUpdates) {
          setProgress(prev => Math.max(prev, endProgress));
          clearInterval(progressTimer);
        } else {
          setProgress(prev => Math.max(prev, newProgress));
        }
      }, updateInterval);

      // Move to next step
      stepTimer = setTimeout(() => {
        clearInterval(progressTimer); // Clean up current timer
        animateStep(stepIndex + 1);
      }, stepDuration);
    };

    // If we have saved progress, resume from the corresponding step
    const saved = Number(sessionStorage.getItem('stripe-loading-progress'));
    const startFrom =
      Number.isFinite(saved) && saved > 0 ? Math.min(Math.floor(saved / 25), steps.length - 1) : 0;

    // Ensure visible progress reflects the starting point
    if (startFrom > 0) {
      const startPct = startFrom * 25;
      setProgress(prev => Math.max(prev, startPct));
    }

    animateStep(startFrom);

    return () => {
      clearInterval(progressTimer);
      clearTimeout(stepTimer);
    };
  }, []);

  return (
    <div
      className="fixed inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-100 z-40 overflow-hidden"
      style={{ left: '256px', top: '64px' }}
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        {/* Floating Stripe-themed elements */}
        <motion.div
          className="absolute top-20 left-20 w-32 h-32 bg-gradient-to-br from-purple-500/10 to-blue-500/10 rounded-full blur-2xl"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.3, 0.6, 0.3],
            x: [0, 30, 0],
            y: [0, -20, 0],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        <motion.div
          className="absolute top-40 right-32 w-24 h-24 bg-gradient-to-br from-teal-500/10 to-green-500/10 rounded-full blur-2xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.4, 0.7, 0.4],
            x: [0, -25, 0],
            y: [0, 25, 0],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1,
          }}
        />

        <motion.div
          className="absolute bottom-32 left-1/3 w-40 h-40 bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.4, 1],
            opacity: [0.2, 0.5, 0.2],
            x: [0, 40, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
        />
      </div>

      {/* Main Loading Content */}
      <div className="relative z-10 flex flex-col items-center justify-center h-screen p-6">
        {/* Stripe Loading Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{
            opacity: 1,
            scale: [1, 1.005, 1],
            y: 0,
          }}
          transition={{
            opacity: { duration: 0.6, ease: 'easeOut' },
            y: { duration: 0.6, ease: 'easeOut' },
            scale: { duration: 4, repeat: Infinity, ease: 'easeInOut' },
          }}
          className={`bg-white/95 backdrop-blur-md rounded-3xl border border-gray-200/60 p-6 shadow-2xl max-w-2xl w-full mx-auto ring-4 ${colors.ring}`}
        >
          {/* Header Section */}
          <div className="text-center mb-3">
            {/* Animated Stripe Logo/Icon */}
            <motion.div
              animate={{
                rotate: [0, 360],
                scale: [1, 1.1, 1],
              }}
              transition={{
                rotate: { duration: 4, repeat: Infinity, ease: 'linear' },
                scale: { duration: 3, repeat: Infinity, ease: 'easeInOut' },
              }}
              className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${colors.gradient} rounded-2xl mb-3 shadow-2xl`}
            >
              <CreditCard className="w-8 h-8 text-white" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="text-xl font-bold text-gray-800 mb-4"
            >
              Loading Stripe Dashboard
            </motion.h1>
          </div>

          {/* Enhanced Progress Bar */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-gray-600">Progress</span>
              <span className="text-sm font-bold text-gray-800">{Math.round(progress)}%</span>
            </div>

            {/* Progress Bar Container */}
            <div className="relative h-3 bg-gray-200 rounded-full overflow-hidden shadow-inner">
              {/* Animated Background Glow */}
              <motion.div
                className={`absolute inset-0 bg-gradient-to-r ${colors.glow} opacity-30 blur-sm`}
                animate={{
                  x: ['-100%', '100%'],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              />

              {/* Main Progress Fill */}
              <motion.div
                className={`h-full bg-gradient-to-r ${colors.gradient} rounded-full shadow-lg relative overflow-hidden`}
                initial={{ width: '0%' }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              >
                {/* Shimmer Effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                  animate={{
                    x: ['-100%', '100%'],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
              </motion.div>

              {/* Progress Dot */}
              <motion.div
                className={`absolute top-1/2 transform -translate-y-1/2 w-4 h-4 ${colors.dot} rounded-full shadow-lg border-2 border-white`}
                animate={{
                  left: `${Math.max(0, progress - 2)}%`,
                }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              />
            </div>
          </div>

          {/* Current Step Display */}
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="text-center"
          >
            <div className="flex items-center justify-center space-x-3 mb-4">
              <motion.div
                animate={{
                  rotate: 360,
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  rotate: { duration: 2, repeat: Infinity, ease: 'linear' },
                  scale: { duration: 1.5, repeat: Infinity, ease: 'easeInOut' },
                }}
                className="relative"
              >
                {steps[currentStep] &&
                  (() => {
                    const IconComponent = steps[currentStep].icon;

                    return <IconComponent className={`w-6 h-6 ${steps[currentStep].color}`} />;
                  })()}
                <motion.div
                  className="absolute inset-0 rounded-full"
                  animate={{
                    boxShadow: [
                      '0 0 0 0 rgba(59, 130, 246, 0.4)',
                      '0 0 0 10px rgba(59, 130, 246, 0)',
                      '0 0 0 0 rgba(59, 130, 246, 0)',
                    ],
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              </motion.div>
              <motion.span
                className="text-lg font-semibold text-gray-700"
                animate={{ opacity: [0.8, 1, 0.8] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              >
                {steps[currentStep]?.text || 'Almost ready...'}
              </motion.span>
            </div>

            {/* Step Indicators */}
            <div className="flex items-center justify-center space-x-2">
              {steps.map((_, index) => (
                <motion.div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index <= currentStep ? `${colors.dot} shadow-md` : 'bg-gray-300'
                  }`}
                  animate={{
                    scale: index === currentStep ? [1, 1.3, 1] : 1,
                  }}
                  transition={{
                    duration: 1,
                    repeat: index === currentStep ? Infinity : 0,
                  }}
                />
              ))}
            </div>
          </motion.div>
        </motion.div>

        {/* Simple Bottom Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.5 }}
          className="mt-6 text-center"
        >
          <motion.p
            className="text-sm text-gray-500"
            animate={{ opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          >
            Please wait while we prepare your dashboard
          </motion.p>
        </motion.div>
      </div>
    </div>
  );
};

export default StripeLoading;
